<template>
  <div class="support-page">
    <!-- O título já vem do cabeçalho da rota; aqui só a linha de contexto. -->
    <p class="support-lead">{{ $t('support.subtitle') }}</p>

    <div class="support-grid">
      <hb-section :title="$t('support.channels_title')">
        <ul class="support-channels">
          <li v-for="channel in channels" :key="channel.email" class="support-channel">
            <feather-icon :icon="channel.icon" class="support-channel__icon" />
            <div class="support-channel__body">
              <span class="support-channel__label">{{ channel.label }}</span>
              <a :href="channel.href" class="support-channel__value">{{ channel.email }}</a>
              <small class="support-channel__hint">{{ channel.hint }}</small>
            </div>
          </li>
        </ul>

        <p class="support-hours">
          <feather-icon icon="ClockIcon" class="mr-1 align-middle" />
          {{ $t('support.hours') }}
        </p>
      </hb-section>

      <hb-section :title="$t('support.before_title')" :subtitle="$t('support.before_subtitle')">
        <ol class="support-checklist">
          <li v-for="(step, index) in checklist" :key="index">{{ step }}</li>
        </ol>

        <div class="support-context">
          <div class="support-context__head">
            <span class="support-context__title">{{ $t('support.context_title') }}</span>
            <hb-copy-button :text="contextText" :allow-share="false" />
          </div>
          <dl class="support-context__list">
            <div v-for="row in contextRows" :key="row.label" class="support-context__row">
              <dt>{{ row.label }}</dt>
              <dd>{{ row.value }}</dd>
            </div>
          </dl>
        </div>
      </hb-section>
    </div>

    <hb-section :title="$t('support.faq_title')" class="mt-4">
      <div class="support-faq">
        <details v-for="(item, index) in faq" :key="index" class="support-faq__item">
          <summary class="support-faq__question">{{ item.q }}</summary>
          <p class="support-faq__answer">{{ item.a }}</p>
        </details>
      </div>
    </hb-section>

    <hb-section :title="$t('support.legal_title')" :subtitle="$t('support.legal_subtitle')" class="mt-4">
      <div class="support-links">
        <router-link v-for="link in legalLinks" :key="link.to" :to="link.to" class="support-link">
          <feather-icon icon="FileTextIcon" class="mr-1" />
          {{ link.label }}
        </router-link>
        <!--
          Reabrir o banner: revogar consentimento é direito do titular (Art. 18,
          IX) e tem de ser tão fácil quanto conceder. Aqui é o único lugar do
          backoffice que já reúne os documentos legais, então é onde a pessoa vai
          procurar.
        -->
        <button type="button" class="support-link" @click="openCookiePreferences">
          <feather-icon icon="SlidersIcon" class="mr-1" />
          {{ $t('support.link_cookie_preferences') }}
        </button>
      </div>
    </hb-section>
  </div>
</template>

<script>
import HbSection from '@/components/hubee/HbSection.vue'
import HbCopyButton from '@/components/hubee/HbCopyButton.vue'
import { reopenPreferences } from '@/util/cookieConsent'

/**
 * Canal de suporte do backoffice.
 *
 * Sem chamado aberto pela tela de propósito: não existe endpoint de tickets, e
 * um formulário que só monta um `mailto` esconde do usuário para onde a mensagem
 * vai. O que a tela faz de útil é reunir os endereços certos (suporte × LGPD) e
 * montar o bloco de contexto técnico — versão, usuário, tela, navegador — que é
 * o que costuma faltar no primeiro e-mail e custa um dia de ida e volta.
 */
export default {
  name: 'Support',
  components: { HbSection, HbCopyButton },
  computed: {
    supportEmail() {
      return 'contato@hubeesports.com'
    },
    privacyEmail() {
      return 'privacidade@hubeesports.com'
    },
    channels() {
      return [
        {
          icon: 'LifeBuoyIcon',
          label: this.$t('support.channel_support'),
          email: this.supportEmail,
          href: `mailto:${this.supportEmail}?subject=${encodeURIComponent(this.$t('support.mail_subject'))}`,
          hint: this.$t('support.channel_support_hint')
        },
        {
          icon: 'LockIcon',
          label: this.$t('support.channel_privacy'),
          email: this.privacyEmail,
          href: `mailto:${this.privacyEmail}`,
          hint: this.$t('support.channel_privacy_hint')
        }
      ]
    },
    checklist() {
      return [
        this.$t('support.step_screen'),
        this.$t('support.step_expected'),
        this.$t('support.step_when'),
        this.$t('support.step_evidence')
      ]
    },
    contextRows() {
      return [
        { label: this.$t('support.context_user'), value: this.userEmail || '—' },
        { label: this.$t('support.context_profile'), value: this.activeProfile || '—' },
        { label: this.$t('support.context_version'), value: this.appVersion || '—' },
        { label: this.$t('support.context_browser'), value: this.browser }
      ]
    },
    /** Mesmo conteúdo do quadro, em texto, para colar no e-mail. */
    contextText() {
      return this.contextRows.map(row => `${row.label}: ${row.value}`).join('\n')
    },
    faq() {
      return [
        { q: this.$t('support.faq_password_q'), a: this.$t('support.faq_password_a') },
        { q: this.$t('support.faq_access_q'), a: this.$t('support.faq_access_a') },
        { q: this.$t('support.faq_payment_q'), a: this.$t('support.faq_payment_a') },
        { q: this.$t('support.faq_data_q'), a: this.$t('support.faq_data_a') }
      ]
    },
    legalLinks() {
      return [
        { to: '/termos', label: this.$t('support.link_terms') },
        { to: '/privacidade', label: this.$t('support.link_privacy') },
        { to: '/cookies', label: this.$t('support.link_cookies') }
      ]
    },
    userEmail() {
      return this.$store.getters.userEmail
    },
    activeProfile() {
      return this.$store.getters.activeProfile
    },
    appVersion() {
      return this.$store.getters.appVersion
    },
    browser() {
      return typeof navigator === 'undefined' ? '—' : navigator.userAgent
    }
  },
  methods: {
    /**
     * O banner só decide se aparece no `mounted()`, e ele é montado uma vez pelo
     * `App.vue`. Recarregar é o jeito mais simples de fazer a pergunta de novo —
     * e é o mesmo caminho usado no rodapé do marketplace.
     */
    openCookiePreferences() {
      reopenPreferences()
      this.$router.go(0)
    }
  }
}
</script>

<style scoped>
.support-lead {
  margin: 0 0 1rem;
  color: var(--text-muted);
}

.support-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 1rem;
}

.support-channels {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 1rem;
}

.support-channel {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}

.support-channel__icon {
  flex: none;
  margin-top: 0.15rem;
  color: var(--primary);
}

.support-channel__body {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.support-channel__label {
  font-weight: 600;
}

.support-channel__value {
  word-break: break-all;
}

.support-channel__hint {
  color: var(--text-muted);
}

.support-hours {
  margin: 1.25rem 0 0;
  color: var(--text-muted);
  font-size: 0.875rem;
}

/* O tema zera o marcador das listas; aqui a ordem é a informação. */
.support-checklist {
  margin: 0;
  padding-left: 1.15rem;
  list-style: decimal outside;
  display: grid;
  gap: 0.4rem;
}

.support-checklist li {
  list-style: inherit;
}

.support-context {
  margin-top: 1.25rem;
  border: 1px solid var(--border-color);
  border-radius: var(--radius-card);
  padding: 0.85rem 1rem;
}

.support-context__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.support-context__title {
  font-weight: 600;
}

.support-context__list {
  margin: 0;
  display: grid;
  gap: 0.35rem;
  font-size: 0.875rem;
}

.support-context__row {
  display: flex;
  gap: 0.5rem;
}

.support-context__row dt {
  flex: none;
  color: var(--text-muted);
  font-weight: 400;
}

.support-context__row dd {
  margin: 0;
  min-width: 0;
  word-break: break-word;
}

.support-faq {
  display: grid;
  gap: 0.5rem;
}

.support-faq__item {
  border: 1px solid var(--border-color);
  border-radius: var(--radius-card);
  padding: 0.75rem 1rem;
}

.support-faq__question {
  cursor: pointer;
  font-weight: 600;
}

.support-faq__answer {
  margin: 0.6rem 0 0;
  color: var(--text-muted);
}

.support-links {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.support-link {
  display: inline-flex;
  align-items: center;
  padding: 0.45rem 0.9rem;
  border: 1px solid var(--border-color);
  border-radius: 2rem;
  font-size: 0.875rem;
}

/*
 * "Preferências de cookies" é <button>, não link: ele reabre o banner, não navega.
 * Sem este reset o navegador pinta o fundo cinza padrão e troca a fonte, e o item
 * destoa dos vizinhos na mesma linha.
 */
button.support-link {
  background: none;
  font-family: inherit;
  color: inherit;
  cursor: pointer;
}
</style>
